import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMaterialManager } from "../hooks/useMaterialManager";
import { useMaterialAccess } from "../hooks/useMaterialAccess";
import { useAuth } from "../hooks/useAuth";
import { useFetch } from "../hooks/useFetch";
import API_ROUTES from "../hooks/apiRoutes";
import HamburgerMenu from "../Components/HamburgerMenu/HamburgerMenu";
import HamburgerButton from "../Components/HamburgerMenu/HamburgerButton";
import { ModalMessage } from "../Components/ModalMessage/ModalMessage";
import Footer from "../Components/Footer/Footer";
import "./MaterialPage.css";

interface Content {
  id: string;
  name: string;
  description?: string;
  type: string;
  url: string;
}

interface Material {
  id: string;
  title: string;
  description: string;
  keywords: string[];
  thumbnailUrl?: string;
  isFree: boolean;
  referenceIds?: string[];
  previewUrl?: string;
  purchaseUrl: string;
  introductionUrl?: string;
  templateHtml?: string;
  contents: Content[];
  groups?: { name: string; contents: Content[] }[];
}

interface GroupSectionProps {
  group: { name: string; contents: Content[] };
  renderContentItem: (content: Content) => React.ReactNode;
}

const GroupSection: React.FC<GroupSectionProps> = ({ group, renderContentItem }) => {
  const [isOpen, setIsOpen] = useState(group.name.toLowerCase() === "conteúdo principal");

  return (
    <div className="group-section" style={{ marginBottom: '15px', border: '1px solid #0077ff', borderRadius: '8px', overflow: 'hidden' }}>
      <div 
        className="group-header" 
        onClick={() => setIsOpen(!isOpen)}
        style={{ 
          padding: '15px', 
          background: 'linear-gradient(135deg, #005792, #0077ff)', 
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          userSelect: 'none'
        }}
      >
        <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#fff' }}>{group.name}</h3>
        <span style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s', color: '#fff' }}>
          ▼
        </span>
      </div>
      {isOpen && (
        <div className="group-contents" style={{ padding: '15px', backgroundColor: '#f5f5f5ff' }}>
          <div className="contents-list">
            {group.contents.map((content) => renderContentItem(content))}
          </div>
        </div>
      )}
    </div>
  );
};

const MaterialPage: React.FC = () => {
  const { materialId } = useParams<{ materialId: string }>();
  const navigate = useNavigate();
  const { fetchMaterialById } = useMaterialManager();
  const { checkMaterialAccess, hasAccess } = useMaterialAccess();
  const { isAuthenticated, logout: authLogout } = useAuth();
  const { fetchWithAuth } = useFetch();

  const [material, setMaterial] = useState<Material | null>(null);
  const [contents, setContents] = useState<Content[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState<Content | null>(null);
  const [previewUrlFailed, setPreviewUrlFailed] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<"image" | "pdf" | null>(null);

  // Helper para verificar se tem preview válido
  const hasValidPreview = (previewUrl: string | null | undefined): boolean => {
    return !!(
      previewUrl &&
      previewUrl !== null &&
      previewUrl !== "" &&
      previewUrl.trim() !== ""
    );
  };

  const loadMaterial = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setPreviewUrlFailed(false); // Reset do estado ao carregar novo material
      setPreviewError(null); // Reset do erro
      setPreviewType(null); // Reset do tipo de preview

      const materialData = await fetchMaterialById(materialId!);

      // A API retorna o template como objeto completo com HTML
      // Priorizar usar o HTML que já vem no objeto template (mais eficiente)
      if (materialData.template?.html) {
        materialData.templateHtml = materialData.template.html;
      } else if (materialData.templateHtml) {
        // Se já tiver templateHtml diretamente, usar
      } else {
        // Fallback: Se não tiver HTML mas tiver templateId, tentar buscar separadamente
        const templateId = materialData.templateId || materialData.template?.id;
        if (templateId) {
          try {
            const templateResponse = await fetchWithAuth(
              API_ROUTES.TEMPLATES.get(templateId)
            );
            if (templateResponse.ok) {
              const templateData = await templateResponse.json();
              materialData.templateHtml = templateData.html;
            }
          } catch (error) {
            // Erro silencioso - template não encontrado
          }
        }
      }

      setMaterial(materialData);
      setContents(materialData.contents || []);
      setPreviewType(null); // Reset do tipo - será detectado na renderização

      // Verificar acesso ao material
      if (isAuthenticated) {
        await checkMaterialAccess(materialData.id, materialData.isFree);
      }
    } catch (err) {
      setError("Erro ao carregar material");
    } finally {
      setLoading(false);
    }
  }, [fetchMaterialById, materialId, isAuthenticated, checkMaterialAccess]);

  useEffect(() => {
    if (materialId) {
      loadMaterial();
    }
  }, [materialId, loadMaterial]);

  // Verificar acesso quando o usuário se autenticar
  useEffect(() => {
    if (isAuthenticated && material && !hasAccess && !material.isFree) {
      checkMaterialAccess(material.id, material.isFree);
    }
  }, [isAuthenticated, material, hasAccess, checkMaterialAccess]);

  // Processar iframes do template após renderização para adicionar permissões
  useEffect(() => {
    if (material?.templateHtml) {
      const templateContent = document.querySelector(".template-content");
      if (templateContent) {
        const iframes = templateContent.querySelectorAll("iframe");
        iframes.forEach((iframe) => {
          const currentAllow = iframe.getAttribute("allow") || "";
          if (!currentAllow.includes("fullscreen")) {
            iframe.setAttribute(
              "allow",
              currentAllow ? `${currentAllow}; fullscreen` : "fullscreen"
            );
          }
          iframe.setAttribute("allowFullScreen", "true");
        });
      }
    }
  }, [material?.templateHtml]);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    authLogout();
    setShowLogoutModal(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handlePreviewContent = (content: Content) => {
    setPreviewContent(content);
    setShowPreviewModal(true);
  };

  const handleDownloadContent = async (content: Content) => {
    try {
      if (!content.url) {
        alert("URL do conteúdo não disponível");
        return;
      }

      // Verificar se a URL é válida
      try {
        new URL(content.url);
      } catch (urlError) {
        alert("URL do conteúdo é inválida");
        return;
      }

      // Tentar download via fetch primeiro (para URLs assinadas)
      try {
        const response = await fetch(content.url);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // Verificar se é um arquivo binário
        const contentType = response.headers.get("content-type") || "";
        const isBinary =
          !contentType.startsWith("text/") &&
          !contentType.includes("application/json");

        if (isBinary) {
          // Para arquivos binários, usar blob
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = content.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Limpar o blob URL
          URL.revokeObjectURL(blobUrl);
        } else {
          // Para arquivos de texto, usar download direto
          const link = document.createElement("a");
          link.href = content.url;
          link.download = content.name;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (fetchError) {
        // Fallback: método direto
        const link = document.createElement("a");
        link.href = content.url;
        link.download = content.name;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erro desconhecido";
      alert(`Erro ao fazer download do arquivo: ${errorMessage}`);
    }
  };

  const renderContentItem = (content: Content) => (
    <div key={content.id} className="content-item">
      <div className="content-icon">
        {content.type === "pdf" && "📄"}
        {content.type === "png" && "🖼️"}
        {content.type === "jpg" && "🖼️"}
        {content.type === "jpeg" && "🖼️"}
        {content.type === "mp4" && "🎥"}
        {content.type === "mp3" && "🎵"}
        {content.type === "txt" && "📝"}
        {content.type === "docx" && "📄"}
        {content.type === "xlsx" && "📊"}
        {content.type === "pptx" && "📊"}
        {![
          "pdf",
          "png",
          "jpg",
          "jpeg",
          "mp4",
          "mp3",
          "txt",
          "docx",
          "xlsx",
          "pptx",
        ].includes(content.type) && "📁"}
      </div>
      <div className="content-details">
        <h3 className="content-name">{content.name}</h3>
        {content.description && (
          <p className="content-description">
            {content.description}
          </p>
        )}
        <span className="content-type">
          {content.type.toUpperCase()}
        </span>
      </div>
      <div className="content-actions">
        {/* Mostrar botões apenas se o usuário tiver acesso ou se o material for gratuito */}
        {!isAuthenticated || hasAccess || material?.isFree ? (
          <>
            <button
              className="preview-btn"
              onClick={() => handlePreviewContent(content)}
              title="Visualizar conteúdo"
            >
              👁️ Preview
            </button>
            <button
              className="download-btn"
              onClick={() => handleDownloadContent(content)}
              title="Download do conteúdo"
            >
              ⬇️ Download
            </button>
          </>
        ) : (
          <div className="no-access-message">
            <span className="no-access-text">
              🔒 Acesso restrito
            </span>
            <p className="no-access-description">
              Faça login e adquira este material para acessar os
              conteúdos
            </p>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando material...</p>
      </div>
    );
  }

  if (error || !material) {
    return (
      <div className="error-container">
        <h2>Material não encontrado</h2>
        <p>
          {error || "O material solicitado não existe ou não está disponível."}
        </p>
        <button onClick={() => navigate("/")} className="retry-button">
          Voltar para Home
        </button>
      </div>
    );
  }

  return (
    <div className="material-page">
      <header className="page-header">
        <div className="header-content">
          <div className="header-left">
            {isAuthenticated && (
              <HamburgerButton isOpen={isMenuOpen} onClick={toggleMenu} />
            )}
            <button
              onClick={() => navigate("/")}
              className="back-button"
              title="Voltar para Home"
            >
              ← Voltar
            </button>
          </div>
          <div className="header-actions">
            <button
              onClick={
                isAuthenticated ? handleLogout : () => navigate("/login")
              }
              className="auth-button"
            >
              {isAuthenticated ? "Sair" : "Entrar"}
            </button>
          </div>
        </div>
      </header>

      {isAuthenticated && (
        <HamburgerMenu isOpen={isMenuOpen} onClose={closeMenu} />
      )}

      {/* Banner de Compra Sticky - Aparece quando não tem acesso */}
      {!hasAccess && !material.isFree && (
        <div className="purchase-banner-sticky">
          <div className="purchase-banner-content">
            <div className="purchase-banner-text">
              <span className="banner-icon">🔒</span>
              <span className="banner-message">
                Este material é exclusivo. Adquira agora para ter acesso
                completo!
              </span>
            </div>
            <button
              onClick={() => {
                const url =
                  material.purchaseUrl ||
                  "https://wa.me/5511999999999?text=Olá!%20Gostaria%20de%20adquirir%20o%20material%20" +
                    encodeURIComponent(material.title);
                window.open(url, "_blank");
              }}
              className="purchase-button"
            >
              💳 Comprar Agora
            </button>
          </div>
        </div>
      )}

      <main className="page-main">
        {/* Header do Material com Thumbnail e Título Sobreposto */}
        <div className="material-header">
          <div className="material-thumbnail-hero">
            {material.thumbnailUrl ? (
              <>
                <img
                  src={material.thumbnailUrl}
                  alt={material.title}
                  className="thumbnail-hero-image"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const placeholder = e.currentTarget
                      .nextElementSibling as HTMLElement;
                    if (placeholder) {
                      placeholder.style.display = "flex";
                    }
                  }}
                />
                <div
                  className="thumbnail-placeholder-hero"
                  style={{ display: "none" }}
                >
                  📚
                  <small>Thumbnail indisponível</small>
                </div>
              </>
            ) : (
              <div className="thumbnail-placeholder-hero">
                📚
                <small>Sem thumbnail</small>
              </div>
            )}
            <div className="thumbnail-gradient-overlay">
              <h1 className="material-title-overlay">{material.title}</h1>
            </div>
          </div>

          {/* Seção de Descrição */}
          <div className="description-section">
            <div className="description-header">
              <h2>Descrição</h2>
            </div>
            <div className="description-content">
              <div dangerouslySetInnerHTML={{ __html: material.description }} />
            </div>
          </div>

          {/* Template HTML - Isolado para não afetar o layout */}
          {material.templateHtml &&
            (() => {
              // Função para escopar CSS ao container do template
              const scopeCSS = (css: string, scopeId: string): string => {
                // Remover comentários
                css = css.replace(/\/\*[\s\S]*?\*\//g, "");

                // Função auxiliar para escopar seletores
                const scopeSelector = (selector: string): string => {
                  selector = selector.trim();
                  // Ignorar seletores que já começam com o escopo
                  if (selector.includes(`.template-content-isolated`)) {
                    return selector;
                  }
                  // Escopar :root, html, body
                  if (
                    selector === ":root" ||
                    selector === "html" ||
                    selector === "body"
                  ) {
                    return `.template-content-isolated ${selector}`;
                  }
                  // Escopar outros seletores
                  return `.template-content-isolated ${selector}`;
                };

                // Processar @media queries separadamente (com suporte a aninhamento)
                const processMediaQuery = (mq: string): string => {
                  // Extrair a condição do @media
                  const mediaMatch = mq.match(/@media\s+([^{]+)\{/);
                  if (!mediaMatch) return mq;

                  const mediaCondition = mediaMatch[1];
                  // Extrair o conteúdo dentro do @media
                  let content = mq.substring(mediaMatch[0].length);
                  content = content.slice(0, -1); // Remover última }

                  // Escopar todas as regras dentro do @media
                  const scopedContent = content.replace(
                    /([^{}]+)\{([^{}]*)\}/g,
                    (
                      match: string,
                      selectors: string,
                      declarations: string
                    ) => {
                      const selectorList = selectors
                        .split(",")
                        .map((s: string) => s.trim())
                        .filter((s: string) => s);
                      const scopedSelectors = selectorList
                        .map(scopeSelector)
                        .join(", ");
                      return `${scopedSelectors} { ${declarations} }`;
                    }
                  );

                  return `@media ${mediaCondition} { ${scopedContent} }`;
                };

                // Extrair e processar @media queries
                const mediaQueries: string[] = [];
                css = css.replace(
                  /@media[^{]*\{[\s\S]*?\}(?=\s*(?:@media|$|[^{]))/g,
                  (match: string) => {
                    mediaQueries.push(match);
                    return "";
                  }
                );

                // Escopar todas as regras CSS normais (não @media)
                css = css.replace(
                  /([^{}]+)\{([^{}]*)\}/g,
                  (match: string, selectors: string, declarations: string) => {
                    // Ignorar se já foi processado ou é regra especial
                    if (selectors.trim().startsWith("@")) {
                      return match;
                    }

                    // Dividir múltiplos seletores
                    const selectorList = selectors
                      .split(",")
                      .map((s: string) => s.trim())
                      .filter((s: string) => s);

                    // Escopar cada seletor
                    const scopedSelectors = selectorList
                      .map(scopeSelector)
                      .join(", ");

                    return `${scopedSelectors} { ${declarations} }`;
                  }
                );

                // Processar e reinserir @media queries escopadas
                const scopedMediaQueries = mediaQueries.map(processMediaQuery);

                return (
                  css +
                  (scopedMediaQueries.length > 0
                    ? "\n" + scopedMediaQueries.join("\n")
                    : "")
                );
              };

              // Função para processar o HTML do template
              const processTemplateHtml = (html: string): string => {
                let processed = html;

                // Remover DOCTYPE se existir
                processed = processed.replace(/<!doctype[^>]*>/gi, "");

                // Extrair conteúdo do <head> (estilos e scripts)
                const headMatch = processed.match(
                  /<head[^>]*>([\s\S]*?)<\/head>/i
                );
                let headContent = "";
                if (headMatch) {
                  headContent = headMatch[1];
                  // Extrair apenas <style> e <script> do head
                  const styleMatch = headContent.match(
                    /<style[^>]*>([\s\S]*?)<\/style>/gi
                  );
                  const scriptMatch = headContent.match(
                    /<script[^>]*>([\s\S]*?)<\/script>/gi
                  );
                  headContent = "";
                  if (styleMatch) {
                    // Escopar cada bloco de estilo
                    styleMatch.forEach((styleTag) => {
                      const styleContent = styleTag.match(
                        /<style[^>]*>([\s\S]*?)<\/style>/i
                      );
                      if (styleContent && styleContent[1]) {
                        const scopedCSS = scopeCSS(
                          styleContent[1],
                          "template-content-isolated"
                        );
                        headContent += `<style>${scopedCSS}</style>`;
                      }
                    });
                  }
                  if (scriptMatch) {
                    headContent += scriptMatch.join("");
                  }
                  // Remover o <head> completo
                  processed = processed.replace(
                    /<head[^>]*>[\s\S]*?<\/head>/i,
                    ""
                  );
                }

                // Extrair conteúdo do <body>
                const bodyMatch = processed.match(
                  /<body[^>]*>([\s\S]*?)<\/body>/i
                );
                let bodyContent = "";
                if (bodyMatch) {
                  bodyContent = bodyMatch[1];
                } else {
                  // Se não tem <body>, pode ser que o conteúdo esteja direto
                  // Remover tags <html> se existirem
                  bodyContent = processed
                    .replace(/<html[^>]*>/gi, "")
                    .replace(/<\/html>/gi, "");
                }

                // Remover tags <html> restantes
                bodyContent = bodyContent
                  .replace(/<html[^>]*>/gi, "")
                  .replace(/<\/html>/gi, "");

                // Combinar: estilos/scripts do head + conteúdo do body
                return headContent + bodyContent;
              };

              const processedHtml = processTemplateHtml(material.templateHtml);

              return (
                <div className="template-html-section">
                  <div className="template-content-wrapper">
                    <div
                      className="template-content template-content-isolated"
                      ref={(el) => {
                        // Processar iframes após o render para adicionar permissões
                        if (el) {
                          const iframes = el.querySelectorAll("iframe");
                          iframes.forEach((iframe) => {
                            const currentAllow =
                              iframe.getAttribute("allow") || "";
                            if (!currentAllow.includes("fullscreen")) {
                              iframe.setAttribute(
                                "allow",
                                currentAllow
                                  ? `${currentAllow}; fullscreen`
                                  : "fullscreen"
                              );
                            }
                            // Garantir que allowFullScreen também está presente
                            iframe.setAttribute("allowFullScreen", "true");
                          });
                        }
                      }}
                      dangerouslySetInnerHTML={{
                        __html: processedHtml.replace(
                          /<iframe([^>]*)>/gi,
                          (match: string, attrs: string) => {
                            // Adicionar permissões de fullscreen aos iframes do template
                            if (!attrs.includes("allow=")) {
                              return `<iframe${attrs} allow="fullscreen" allowFullScreen>`;
                            } else if (!attrs.includes("fullscreen")) {
                              return `<iframe${attrs.replace(
                                /allow="([^"]*)"/,
                                (_m: string, p1: string) =>
                                  `allow="${p1}; fullscreen"`
                              )} allowFullScreen>`;
                            }
                            // Se já tem fullscreen, garantir que allowFullScreen também está
                            if (!attrs.includes("allowFullScreen")) {
                              return `<iframe${attrs} allowFullScreen>`;
                            }
                            return match;
                          }
                        ),
                      }}
                    />
                  </div>
                </div>
              );
            })()}

          {/* Vídeo de Introdução */}
          {material.introductionUrl && (
            <div className="introduction-video-section">
              <div className="introduction-header">
                <h3>Vídeo de Introdução</h3>
              </div>
              <div className="introduction-video-container">
                {(() => {
                  // Detectar se é URL do YouTube
                  const isYouTube =
                    material.introductionUrl.includes("youtube.com") ||
                    material.introductionUrl.includes("youtu.be");

                  if (isYouTube) {
                    // Extrair o ID do vídeo do YouTube
                    let videoId = "";

                    if (
                      material.introductionUrl.includes("youtube.com/watch?v=")
                    ) {
                      videoId = material.introductionUrl
                        .split("watch?v=")[1]
                        ?.split("&")[0];
                    } else if (material.introductionUrl.includes("youtu.be/")) {
                      videoId = material.introductionUrl
                        .split("youtu.be/")[1]
                        ?.split("?")[0];
                    } else if (
                      material.introductionUrl.includes("youtube.com/embed/")
                    ) {
                      videoId = material.introductionUrl
                        .split("embed/")[1]
                        ?.split("?")[0];
                    }

                    if (videoId) {
                      return (
                        <iframe
                          className="introduction-video youtube-video"
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title="Vídeo de Introdução"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                          allowFullScreen
                        />
                      );
                    }
                  }

                  // Se não for YouTube, usar tag <video> normal
                  return (
                    <video
                      controls
                      className="introduction-video"
                      poster={material.thumbnailUrl}
                    >
                      <source src={material.introductionUrl} type="video/mp4" />
                      Seu navegador não suporta a tag de vídeo.
                    </video>
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Preview Section */}
        {hasValidPreview(material.previewUrl) && (
          <div className="material-preview-section">
            <h3>Preview do Material</h3>
            <div
              className={`preview-container ${
                material.previewUrl!.includes(".pdf") ? "pdf-preview" : ""
              }`}
            >
              <div className="preview-content">
                {!previewUrlFailed && material.previewUrl ? (
                  // Se tiver previewUrl, tentar renderizar como PDF primeiro (se previewType for null ou pdf)
                  // Tentar PDF primeiro (quando previewType for null ou pdf)
                  // Se falhar, tentar como imagem
                  previewType !== "image" ? (
                    <iframe
                      src={`${material.previewUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
                      className="pdf-preview-iframe"
                      title="Preview do Material"
                      style={{
                        width: "100%",
                        height: "100%",
                        minHeight: "600px",
                        border: "none",
                        borderRadius: "8px",
                      }}
                      onError={() => {
                        // Se o iframe PDF falhou e ainda não tentou como imagem, tentar imagem
                        if (previewType === null || previewType === "pdf") {
                          setPreviewType("image");
                          setPreviewUrlFailed(false);
                        } else {
                          // Se já tentou ambos, usar fallback
                          setPreviewUrlFailed(true);
                        }
                      }}
                      onLoad={() => {
                        // Se o iframe carregou, é PDF
                        if (previewType !== "pdf") {
                          setPreviewType("pdf");
                        }
                        setPreviewUrlFailed(false);
                      }}
                    />
                  ) : (
                    // Tentar renderizar como imagem
                    // Se falhar e ainda não soubermos o tipo, tentar como PDF
                    <img
                      key={`preview-${material.previewUrl}`}
                      src={material.previewUrl}
                      alt="Preview do Material"
                      style={{ maxWidth: "100%", height: "auto" }}
                      onError={(e) => {
                        // Se a imagem falhou e ainda não tentou como PDF, tentar PDF
                        if (previewType === null) {
                          setPreviewType("pdf");
                          setPreviewUrlFailed(false);
                        } else {
                          // Se já tentou ambos e falhou, usar fallback
                          setPreviewUrlFailed(true);
                        }
                      }}
                      onLoad={() => {
                        // Se a imagem carregou, é uma imagem
                        if (previewType !== "image") {
                          setPreviewType("image");
                        }
                        setPreviewUrlFailed(false);
                      }}
                    />
                  )
                ) : (
                  <div
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#666",
                    }}
                  >
                    <p>Sem preview disponível</p>
                  </div>
                )}
              </div>
              <div className="watermark-overlay">
                <div className="watermark-text">PREVIEW</div>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Conteúdos */}
        <div className="contents-section">
          <h2>Conteúdos do Material</h2>

          {material?.groups && material.groups.length > 0 ? (
             <div className="groups-list">
               {material.groups.map((group, index) => (
                 <GroupSection 
                   key={index} 
                   group={group} 
                   renderContentItem={renderContentItem} 
                 />
               ))}
             </div>
          ) : contents.length > 0 ? (
            <div className="contents-list">
              {contents.map((content) => renderContentItem(content))}
            </div>
          ) : (
            <div className="no-contents">
              <h3>Nenhum conteúdo disponível</h3>
              <p>Este material ainda não possui conteúdos.</p>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Modal de Logout */}
      {showLogoutModal && (
        <ModalMessage
          title="Tem certeza?"
          description="Deseja realmente sair da sua conta?"
          leftButtonText="Sim"
          leftButtonAction={confirmLogout}
          rightButtonText="Não"
          rightButtonAction={() => setShowLogoutModal(false)}
        />
      )}

      {/* Modal de Preview de Conteúdo */}
      {showPreviewModal && previewContent && (
        <div
          className="modal-overlay"
          onClick={() => setShowPreviewModal(false)}
        >
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Preview: {previewContent.name}</h2>
              <button
                className="close-button"
                onClick={() => setShowPreviewModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="preview-content-info">
                <div className="content-details">
                  <p>
                    <strong>Tipo:</strong> {previewContent.type.toUpperCase()}
                  </p>
                  {previewContent.description && (
                    <p>
                      <strong>Descrição:</strong> {previewContent.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="preview-content-area">
                {previewContent.type === "png" ||
                previewContent.type === "jpg" ||
                previewContent.type === "jpeg" ? (
                  <div className="image-preview">
                    <img
                      src={previewContent.url}
                      alt={previewContent.name}
                      className="preview-image"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const nextElement = e.currentTarget
                          .nextElementSibling as HTMLElement;
                        if (nextElement) {
                          nextElement.style.display = "flex";
                        }
                      }}
                    />
                    <div className="preview-error" style={{ display: "none" }}>
                      <p>Erro ao carregar imagem</p>
                    </div>
                  </div>
                ) : previewContent.type === "pdf" ? (
                  <div className="pdf-preview">
                    <iframe
                      src={previewContent.url}
                      className="preview-pdf"
                      title={previewContent.name}
                    />
                  </div>
                ) : previewContent.type === "txt" ? (
                  <div className="text-preview">
                    <iframe
                      src={previewContent.url}
                      className="preview-text"
                      title={previewContent.name}
                    />
                  </div>
                ) : previewContent.type === "mp4" ? (
                  <div className="video-preview">
                    <video
                      src={previewContent.url}
                      className="preview-video"
                      controls
                      title={previewContent.name}
                    />
                  </div>
                ) : previewContent.type === "mp3" ? (
                  <div className="audio-preview">
                    <audio
                      src={previewContent.url}
                      className="preview-audio"
                      controls
                      title={previewContent.name}
                    />
                  </div>
                ) : (
                  <div className="file-preview">
                    <div className="preview-placeholder">
                      <p>Preview não disponível para este tipo de arquivo</p>
                      <a
                        href={previewContent.url}
                        download={previewContent.name}
                        className="download-link"
                      >
                        ⬇️ Download {previewContent.name}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialPage;
